import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { configsApi, plansApi, settingsApi, cloudpePlansApi } from '../lib/api';
import {
  Save, ArrowLeft, Plus, Trash2, AlertCircle, Check,
  Cpu, HardDrive, DollarSign, Settings, Layers, TrendingUp,
  Monitor, Server, Package, Database
} from 'lucide-react';

// Default configuration values
const defaultConfig = {
  name: 'New Server Config',
  cpuCost: 0, cpuCores: 0, ramCost: 0, ramGB: 0,
  nicCost: 0, chassisCost: 0, otherHardwareCost: 0,
  storageDiskCost: 0, storageDiskCount: 0, storageDiskSizeGB: 0, storageType: 'SSD',
  virtuozzoComputeLicensePerCore: 0, virtuozzoStorageLicensePerTB: 0, otherLicenseCost: 0,
  colocationCostMonthly: 0, internetCostMonthly: 0, supportCostMonthly: 0, salesCostPercentage: 10,
  powerConsumptionWatts: 0, powerCostPerKWH: 0,
  haPercentage: 20,
  // Loan/Finance EMI parameters
  loanInterestRate: 12, // Annual interest rate %
  financePeriodMonths: 36, // Loan tenure in months
  salvageValuePercentage: 10, // Expected salvage value at end of period %
  roiPercentage: 15, // Cost of capital / target ROI %
  annualColocationIncreasePercentage: 5,
  cpuOvercommitRatio: 4, ramOvercommitRatio: 1.5,
  hardwareCurrency: 'USD', operationalCurrency: 'INR',
  isTemplate: false, isShared: false,
};

const defaultVMPlan = {
  name: 'Basic VM', vCPU: 1, ramGB: 1, storageGB: 20,
  bandwidthTBMonthly: 1, ipAddresses: 1, backupIncluded: false,
};

const currencySymbols = { USD: '$', INR: '₹', EUR: '€', GBP: '£' };

// Storage redundancy types with overhead percentages
const storageRedundancyTypes = [
  { id: 'rep2', name: '2x Replication', overhead: 100, description: 'Each block stored twice for redundancy' },
  { id: 'rep3', name: '3x Replication', overhead: 200, description: 'Each block stored three times for high durability' },
  { id: 'ec31', name: 'EC 3+1', overhead: 33, description: 'Erasure coding: 3 data + 1 parity chunk' },
  { id: 'ec32', name: 'EC 3+2', overhead: 67, description: 'Erasure coding: 3 data + 2 parity chunks' },
  { id: 'ec52', name: 'EC 5+2', overhead: 40, description: 'Erasure coding: 5 data + 2 parity chunks' },
  { id: 'ec72', name: 'EC 7+2', overhead: 29, description: 'Erasure coding: 7 data + 2 parity chunks' },
  { id: 'ec173', name: 'EC 17+3', overhead: 18, description: 'Erasure coding: 17 data + 3 parity chunks' },
];

// Input component - defined outside to prevent re-creation on each render
function InputField({ label, value, onChange, type = 'number', suffix, prefix, min = 0, step = 'any' }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs text-slate-400 mb-1 font-medium tracking-wide uppercase">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          min={min}
          step={step}
          className={`w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-2.5 text-slate-200
            focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all
            ${prefix ? 'pl-8 pr-3' : 'px-3'} ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs text-slate-400 mb-1 font-medium tracking-wide uppercase">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2.5 text-slate-200
          focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function TabButton({ active, onClick, children, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
        active
          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
      }`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// Tooltip component for calculation explanations - click to toggle, positioned smartly
function CalcTooltip({ value, explanation, breakdown, children, formatFn }) {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = React.useRef(null);
  const tooltipRef = React.useRef(null);

  // Close on click outside
  React.useEffect(() => {
    if (!isVisible) return;
    const handleClickOutside = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible]);

  // Close on escape
  React.useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible]);

  return (
    <span className="relative inline">
      <span
        ref={triggerRef}
        className="cursor-help border-b border-dashed border-slate-500 hover:border-amber-400 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setIsVisible(!isVisible);
        }}
      >
        {children}
      </span>
      {isVisible && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsVisible(false)} />
          {/* Tooltip - centered on screen for reliability */}
          <div
            ref={tooltipRef}
            className="fixed z-[9999] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 max-w-[90vw]"
          >
            <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl p-4 text-left">
              <div className="flex justify-between items-start mb-2">
                <div className="text-xs text-amber-400 font-semibold uppercase tracking-wide">How this is calculated</div>
                <button
                  onClick={() => setIsVisible(false)}
                  className="text-slate-500 hover:text-slate-300 text-lg leading-none"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-slate-300 mb-3">{explanation}</p>
              {breakdown && breakdown.length > 0 && (
                <div className="space-y-1.5 text-xs border-t border-slate-700 pt-2 mt-2">
                  {breakdown.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center gap-2">
                      <span className="text-slate-400">{item.label}</span>
                      <span className="font-mono text-slate-200 text-right">{formatFn ? formatFn(item.value) : item.value}</span>
                    </div>
                  ))}
                  {breakdown.length > 1 && (
                    <div className="flex justify-between items-center pt-1.5 border-t border-slate-700 font-semibold">
                      <span className="text-slate-300">Result</span>
                      <span className="font-mono text-amber-400">{formatFn ? formatFn(value) : value}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// Transform database snake_case to camelCase
const transformConfig = (dbConfig) => ({
  name: dbConfig.name,
  cpuCost: dbConfig.cpu_cost,
  cpuCores: dbConfig.cpu_cores,
  ramCost: dbConfig.ram_cost,
  ramGB: dbConfig.ram_gb,
  nicCost: dbConfig.nic_cost,
  chassisCost: dbConfig.chassis_cost,
  otherHardwareCost: dbConfig.other_hardware_cost,
  storageDiskCost: dbConfig.storage_disk_cost,
  storageDiskCount: dbConfig.storage_disk_count,
  storageDiskSizeGB: dbConfig.storage_disk_size_gb,
  storageType: dbConfig.storage_type,
  virtuozzoComputeLicensePerCore: dbConfig.virtuozzo_compute_license_per_core,
  virtuozzoStorageLicensePerTB: dbConfig.virtuozzo_storage_license_per_tb,
  otherLicenseCost: dbConfig.other_license_cost,
  colocationCostMonthly: dbConfig.colocation_cost_monthly,
  internetCostMonthly: dbConfig.internet_cost_monthly,
  supportCostMonthly: dbConfig.support_cost_monthly,
  salesCostPercentage: dbConfig.sales_cost_percentage,
  powerConsumptionWatts: dbConfig.power_consumption_watts,
  powerCostPerKWH: dbConfig.power_cost_per_kwh,
  haPercentage: dbConfig.ha_percentage,
  loanInterestRate: dbConfig.loan_interest_rate ?? 12,
  financePeriodMonths: dbConfig.finance_period_months ?? dbConfig.amortization_months ?? 36,
  salvageValuePercentage: dbConfig.salvage_value_percentage ?? 10,
  roiPercentage: dbConfig.roi_percentage,
  annualColocationIncreasePercentage: dbConfig.annual_colocation_increase_percentage,
  cpuOvercommitRatio: dbConfig.cpu_overcommit_ratio,
  ramOvercommitRatio: dbConfig.ram_overcommit_ratio,
  hardwareCurrency: dbConfig.hardware_currency,
  operationalCurrency: dbConfig.operational_currency,
  isTemplate: dbConfig.is_template,
  isShared: dbConfig.is_shared,
});

const transformVMPlan = (dbPlan) => ({
  id: dbPlan.id,
  name: dbPlan.name,
  vCPU: dbPlan.vcpu,
  ramGB: dbPlan.ram_gb,
  storageGB: dbPlan.storage_gb,
  bandwidthTBMonthly: dbPlan.bandwidth_tb_monthly,
  ipAddresses: dbPlan.ip_addresses,
  backupIncluded: dbPlan.backup_included,
});

function ConfigEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [config, setConfig] = useState(defaultConfig);
  const [vmPlans, setVmPlans] = useState([{ ...defaultVMPlan, id: Date.now() }]);
  const [settings, setSettings] = useState({
    usdInrRate: 83.5, eurInrRate: 90.5, gbpInrRate: 105.5,
    targetCurrency: 'INR', profitMarginPercentage: 30,
    bandwidthCostPerTB: 500, ipAddressCost: 100, backupCostPerGB: 2,
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('hardware');

  // CloudPE Plans state
  const [cloudpePlans, setCloudpePlans] = useState([]);
  const [selectedPlanOS, setSelectedPlanOS] = useState('linux');
  const [selectedCloudPlanType, setSelectedCloudPlanType] = useState('general_purpose');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load user settings
        const settingsData = await settingsApi.get();
        if (settingsData.settings) {
          setSettings({
            usdInrRate: settingsData.settings.usd_inr_rate,
            eurInrRate: settingsData.settings.eur_inr_rate,
            gbpInrRate: settingsData.settings.gbp_inr_rate,
            targetCurrency: settingsData.settings.target_currency,
            profitMarginPercentage: settingsData.settings.profit_margin_percentage,
            bandwidthCostPerTB: settingsData.settings.bandwidth_cost_per_tb,
            ipAddressCost: settingsData.settings.ip_address_cost,
            backupCostPerGB: settingsData.settings.backup_cost_per_gb,
          });
        }

        // Load CloudPE plans
        try {
          const plansData = await cloudpePlansApi.list();
          if (plansData.plans) {
            setCloudpePlans(plansData.plans);
          }
        } catch (planErr) {
          console.warn('Failed to load CloudPE plans:', planErr);
        }

        // Load config if editing
        if (!isNew) {
          const data = await configsApi.get(id);
          setConfig(transformConfig(data.config));
          if (data.vmPlans && data.vmPlans.length > 0) {
            setVmPlans(data.vmPlans.map(transformVMPlan));
          }
        }
      } catch (err) {
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isNew]);

  // Convert currency
  const convertToTargetCurrency = (amount, fromCurrency) => {
    if (fromCurrency === settings.targetCurrency) return amount;
    if (fromCurrency === 'USD' && settings.targetCurrency === 'INR') return amount * settings.usdInrRate;
    if (fromCurrency === 'INR' && settings.targetCurrency === 'USD') return amount / settings.usdInrRate;
    if (fromCurrency === 'EUR' && settings.targetCurrency === 'INR') return amount * settings.eurInrRate;
    if (fromCurrency === 'GBP' && settings.targetCurrency === 'INR') return amount * settings.gbpInrRate;
    return amount;
  };

  const formatCurrency = (amount) => {
    return `${currencySymbols[settings.targetCurrency]}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Calculate all costs - split into Compute and Storage
  const costs = useMemo(() => {
    // === COMPUTE HARDWARE (everything except storage disks) ===
    const computeHardwareCost = convertToTargetCurrency(
      config.cpuCost + config.ramCost + config.nicCost +
      config.chassisCost + config.otherHardwareCost,
      config.hardwareCurrency
    );

    // === STORAGE HARDWARE (only storage disks) ===
    const storageHardwareCost = convertToTargetCurrency(
      config.storageDiskCost * config.storageDiskCount,
      config.hardwareCurrency
    );

    // Total hardware (for display)
    const totalHardwareCost = computeHardwareCost + storageHardwareCost;

    // HA Reserve - only for compute (not storage)
    const haCost = computeHardwareCost * (config.haPercentage / 100);

    // Capital costs
    const computeCapitalCost = computeHardwareCost + haCost;
    const storageCapitalCost = storageHardwareCost; // No HA for storage
    const totalCapitalCost = computeCapitalCost + storageCapitalCost;

    // === LICENSE COSTS ===
    const computeLicenseCostAnnual = convertToTargetCurrency(
      config.virtuozzoComputeLicensePerCore * config.cpuCores * 12, 'USD'
    );

    const totalStorageTB = (config.storageDiskSizeGB * config.storageDiskCount) / 1024;
    const storageLicenseCostAnnual = convertToTargetCurrency(
      config.virtuozzoStorageLicensePerTB * totalStorageTB * 12, 'USD'
    );

    const otherLicenseCostAnnual = convertToTargetCurrency(config.otherLicenseCost * 12, 'USD');

    // === OPERATIONAL COSTS (will be split) ===
    const monthlyColocation = convertToTargetCurrency(config.colocationCostMonthly, config.operationalCurrency);
    const monthlyInternet = convertToTargetCurrency(config.internetCostMonthly, config.operationalCurrency);
    const monthlySupport = convertToTargetCurrency(config.supportCostMonthly, config.operationalCurrency);
    const monthlyPowerCost = (config.powerConsumptionWatts / 1000) * 24 * 30 * config.powerCostPerKWH;

    // === EMI CALCULATION - Separate for Compute and Storage ===
    const monthlyInterestRate = (config.loanInterestRate / 100) / 12;
    const n = config.financePeriodMonths || 36;

    // Helper function to calculate EMI
    const calculateEMI = (principal, salvagePercent) => {
      const salvage = principal * (salvagePercent / 100);
      const amountToFinance = principal - salvage;
      if (amountToFinance <= 0 || n <= 0) return { emi: 0, salvage, principal: amountToFinance };
      if (monthlyInterestRate > 0) {
        const factor = Math.pow(1 + monthlyInterestRate, n);
        return { emi: amountToFinance * monthlyInterestRate * factor / (factor - 1), salvage, principal: amountToFinance };
      }
      return { emi: amountToFinance / n, salvage, principal: amountToFinance };
    };

    // Compute EMI (on compute capital with HA)
    const computeEMICalc = calculateEMI(computeCapitalCost, config.salvageValuePercentage);
    const computeMonthlyEMI = computeEMICalc.emi;
    const computeSalvageValue = computeEMICalc.salvage;
    const computePrincipalToFinance = computeEMICalc.principal;

    // Storage EMI (on storage capital, no HA)
    const storageEMICalc = calculateEMI(storageCapitalCost, config.salvageValuePercentage);
    const storageMonthlyEMI = storageEMICalc.emi;
    const storageSalvageValue = storageEMICalc.salvage;
    const storagePrincipalToFinance = storageEMICalc.principal;

    // Total EMI (for display)
    const monthlyEMI = computeMonthlyEMI + storageMonthlyEMI;
    const salvageValue = computeSalvageValue + storageSalvageValue;
    const principalToFinance = computePrincipalToFinance + storagePrincipalToFinance;

    // === SPLIT OPERATIONAL COSTS ===
    // Power: 90% compute, 10% storage
    const computePowerCost = monthlyPowerCost * 0.9;
    const storagePowerCost = monthlyPowerCost * 0.1;

    // Colocation: 80% compute, 20% storage
    const computeColocationCost = monthlyColocation * 0.8;
    const storageColocationCost = monthlyColocation * 0.2;

    // Support: 80% compute, 20% storage
    const computeSupportCost = monthlySupport * 0.8;
    const storageSupportCost = monthlySupport * 0.2;

    // Internet: 100% compute
    const computeInternetCost = monthlyInternet;

    // Licenses
    const computeMonthlyLicenseCost = (computeLicenseCostAnnual + otherLicenseCostAnnual) / 12;
    const storageMonthlyLicenseCost = storageLicenseCostAnnual / 12;
    const monthlyLicenseCost = computeMonthlyLicenseCost + storageMonthlyLicenseCost;

    // === TOTAL MONTHLY COSTS ===
    const computeMonthlyOperatingCost =
      computeMonthlyEMI + computeColocationCost + computeInternetCost +
      computeSupportCost + computePowerCost + computeMonthlyLicenseCost;

    // Storage monthly cost EXCLUDES license (license is per usable TB, calculated in Storage Pricing)
    const storageMonthlyInfraCost =
      storageMonthlyEMI + storageColocationCost +
      storageSupportCost + storagePowerCost;

    const totalMonthlyOperatingCost = computeMonthlyOperatingCost + storageMonthlyInfraCost + storageMonthlyLicenseCost;

    // === AVAILABLE RESOURCES (CPU/RAM only, storage handled in Storage Pricing tab) ===
    const availableCPUCores = config.cpuCores * config.cpuOvercommitRatio;
    const availableRAMGB = config.ramGB * config.ramOvercommitRatio;

    // === RESOURCE UNIT COSTS ===
    // VM pricing uses COMPUTE cost only (CPU 55%, RAM 45%)
    const cpuCostPerCoreMonth = availableCPUCores > 0 ? computeMonthlyOperatingCost / availableCPUCores * 0.55 : 0;
    const ramCostPerGBMonth = availableRAMGB > 0 ? computeMonthlyOperatingCost / availableRAMGB * 0.45 : 0;

    // === 5-YEAR PROJECTIONS ===
    const futureProjections = [];
    let cumulativeColocationIncrease = 1;
    for (let year = 1; year <= 5; year++) {
      cumulativeColocationIncrease *= (1 + config.annualColocationIncreasePercentage / 100);
      const adjustedColocation = monthlyColocation * cumulativeColocationIncrease;
      const yearlyOperatingCost = (totalMonthlyOperatingCost - monthlyColocation + adjustedColocation) * 12;
      futureProjections.push({ year, monthlyColocation: adjustedColocation, yearlyOperatingCost });
    }

    // === STORAGE REDUNDANCY PRICING ===
    const rawStorageGB = config.storageDiskSizeGB * config.storageDiskCount;
    // License cost per TB (converted to target currency)
    const licenseCostPerTBMonth = convertToTargetCurrency(config.virtuozzoStorageLicensePerTB, 'USD');

    const storageRedundancyPricing = storageRedundancyTypes.map(type => {
      const usableStorageGB = rawStorageGB / (1 + type.overhead / 100);
      const usableStorageTB = usableStorageGB / 1024;

      // Infrastructure cost per GB (EMI + colo + support + power, NO license)
      const infraCostPerGBMonth = usableStorageGB > 0 ? storageMonthlyInfraCost / usableStorageGB : 0;

      // License cost per GB (based on usable storage)
      const licensePerGBMonth = licenseCostPerTBMonth / 1024; // Convert per TB to per GB

      // Total cost per GB = Infra + License
      const totalCostPerGBMonth = infraCostPerGBMonth + licensePerGBMonth;

      // With profit margin (on total cost)
      const sellPerGBMonth = totalCostPerGBMonth * (1 + settings.profitMarginPercentage / 100);

      return {
        ...type,
        rawStorageGB,
        rawStorageTB: rawStorageGB / 1024,
        usableStorageGB,
        usableStorageTB,
        infraCostPerGBMonth,
        licensePerGBMonth,
        totalCostPerGBMonth,
        sellPerGBMonth,
        // TB versions
        infraCostPerTBMonth: infraCostPerGBMonth * 1024,
        licensePerTBMonth: licenseCostPerTBMonth,
        totalCostPerTBMonth: totalCostPerGBMonth * 1024,
        sellPerTBMonth: sellPerGBMonth * 1024,
      };
    });

    return {
      // Hardware
      computeHardwareCost, storageHardwareCost, totalHardwareCost,
      haCost, computeCapitalCost, storageCapitalCost, totalCapitalCost,
      // Licenses
      computeLicenseCostAnnual, storageLicenseCostAnnual,
      computeMonthlyLicenseCost, storageMonthlyLicenseCost, monthlyLicenseCost,
      licenseCostPerTBMonth,
      // Operational (totals)
      monthlyColocation, monthlyInternet, monthlySupport, monthlyPowerCost,
      // Operational (split)
      computeColocationCost, storageColocationCost,
      computeSupportCost, storageSupportCost,
      computePowerCost, storagePowerCost,
      computeInternetCost,
      // EMI
      computeMonthlyEMI, storageMonthlyEMI, monthlyEMI,
      computeSalvageValue, storageSalvageValue, salvageValue,
      computePrincipalToFinance, storagePrincipalToFinance, principalToFinance,
      // Monthly totals
      computeMonthlyOperatingCost, storageMonthlyInfraCost, totalMonthlyOperatingCost,
      // Resources (CPU/RAM only)
      availableCPUCores, availableRAMGB,
      cpuCostPerCoreMonth, ramCostPerGBMonth,
      // Other
      futureProjections, totalStorageTB,
      // Storage Redundancy Pricing
      rawStorageGB, storageRedundancyPricing,
    };
  }, [config, settings]);

  // Calculate VM price (storage excluded - only CPU and RAM)
  const calculateVMPrice = (vmPlan) => {
    // Compute cost (CPU + RAM only, storage excluded)
    const cpuCost = vmPlan.vCPU * costs.cpuCostPerCoreMonth;
    const ramCost = vmPlan.ramGB * costs.ramCostPerGBMonth;
    const baseCost = cpuCost + ramCost;

    const bandwidthCost = vmPlan.bandwidthTBMonthly * settings.bandwidthCostPerTB;
    const ipCost = vmPlan.ipAddresses * settings.ipAddressCost;

    const totalCost = baseCost + bandwidthCost + ipCost;
    const salesCost = totalCost * (config.salesCostPercentage / 100);
    const costPrice = totalCost + salesCost;
    const sellingPrice = costPrice * (1 + settings.profitMarginPercentage / 100);

    return {
      cpuCost, ramCost, baseCost,
      bandwidthCost, ipCost,
      salesCost, costPrice, sellingPrice,
      // Breakdown for tooltip
      vCPU: vmPlan.vCPU,
      ramGB: vmPlan.ramGB,
      cpuCostPerCore: costs.cpuCostPerCoreMonth,
      ramCostPerGB: costs.ramCostPerGBMonth,
    };
  };

  // Calculate CloudPE plan pricing and capacity analysis
  const cloudpePlanAnalysis = useMemo(() => {
    const filteredPlans = cloudpePlans.filter(
      plan => plan.os_type === selectedPlanOS && plan.plan_type === selectedCloudPlanType
    );

    return filteredPlans.map(plan => {
      // Calculate how many VMs of this flavor can fit in the server (storage excluded)
      const maxByVCPU = costs.availableCPUCores > 0 ? Math.floor(costs.availableCPUCores / plan.vcpu) : 0;
      const maxByRAM = costs.availableRAMGB > 0 ? Math.floor(costs.availableRAMGB / plan.ram_gb) : 0;
      // Storage excluded from capacity calculation
      const maxVMs = Math.min(maxByVCPU, maxByRAM);

      // Cost price = COMPUTE monthly cost ÷ number of VMs that fit (storage excluded)
      const baseCostPerVM = maxVMs > 0 ? costs.computeMonthlyOperatingCost / maxVMs : 0;
      const salesCost = baseCostPerVM * (config.salesCostPercentage / 100);
      const costPrice = baseCostPerVM + salesCost;
      const sellingPrice = costPrice * (1 + settings.profitMarginPercentage / 100);

      // CloudPE's monthly price (convert if needed - stored in INR)
      const cloudpePrice = settings.targetCurrency === 'INR'
        ? plan.monthly_price_inr
        : plan.monthly_price_inr / settings.usdInrRate;

      // Margin analysis (comparing our cost vs CloudPE price)
      const marginAmount = cloudpePrice - costPrice;
      const marginPercentage = costPrice > 0 ? ((cloudpePrice - costPrice) / costPrice) * 100 : 0;

      // Determine bottleneck resource (storage excluded)
      let bottleneck = 'None';
      if (maxVMs > 0) {
        if (maxVMs === maxByVCPU) bottleneck = 'vCPU';
        else if (maxVMs === maxByRAM) bottleneck = 'RAM';
      }

      // Calculate resource utilization and waste if all maxVMs are deployed
      const usedVCPU = maxVMs * plan.vcpu;
      const usedRAM = maxVMs * plan.ram_gb;

      const wastedVCPU = costs.availableCPUCores - usedVCPU;
      const wastedRAM = costs.availableRAMGB - usedRAM;

      const vcpuUtilization = costs.availableCPUCores > 0 ? (usedVCPU / costs.availableCPUCores) * 100 : 0;
      const ramUtilization = costs.availableRAMGB > 0 ? (usedRAM / costs.availableRAMGB) * 100 : 0;

      // Total potential revenue if all VMs sold at CloudPE price
      const potentialRevenue = maxVMs * cloudpePrice;
      const potentialCost = maxVMs * costPrice;
      const potentialProfit = potentialRevenue - potentialCost;

      return {
        ...plan,
        costPrice,
        sellingPrice,
        cloudpePrice,
        marginAmount,
        marginPercentage,
        maxVMs,
        maxByVCPU,
        maxByRAM,
        bottleneck,
        usedVCPU, usedRAM,
        wastedVCPU, wastedRAM,
        vcpuUtilization, ramUtilization,
        potentialRevenue, potentialCost, potentialProfit,
        baseCostPerVM,
        salesCost,
      };
    });
  }, [cloudpePlans, selectedPlanOS, selectedCloudPlanType, costs, config.salesCostPercentage, settings]);

  // Save handler
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let configId = id;
      
      if (isNew) {
        const data = await configsApi.create(config);
        configId = data.config.id;
      } else {
        await configsApi.update(id, config);
      }

      // Save VM plans
      await plansApi.bulkUpdate(configId, vmPlans);

      setSuccess('Configuration saved successfully!');
      
      if (isNew) {
        navigate(`/config/${configId}`, { replace: true });
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const addVMPlan = () => {
    setVmPlans([...vmPlans, { ...defaultVMPlan, id: Date.now(), name: `VM Plan ${vmPlans.length + 1}` }]);
  };

  const updateVMPlan = (planId, field, value) => {
    setVmPlans(plans => plans.map(p => p.id === planId ? { ...p, [field]: value } : p));
  };

  const deleteVMPlan = (planId) => {
    if (vmPlans.length === 1) return;
    setVmPlans(plans => plans.filter(p => p.id !== planId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <input
              type="text"
              value={config.name}
              onChange={(e) => updateConfig('name', e.target.value)}
              className="text-2xl font-bold text-white bg-transparent border-none focus:outline-none 
                focus:ring-2 focus:ring-amber-500/30 rounded px-2 -ml-2"
              placeholder="Configuration Name"
            />
            <p className="text-slate-400 text-sm mt-1">
              {isNew ? 'Create new configuration' : 'Edit configuration'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 
            text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400">
          <Check className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            <TabButton active={activeTab === 'hardware'} onClick={() => setActiveTab('hardware')} icon={Cpu}>
              Hardware
            </TabButton>
            <TabButton active={activeTab === 'storage'} onClick={() => setActiveTab('storage')} icon={HardDrive}>
              Storage
            </TabButton>
            <TabButton active={activeTab === 'licenses'} onClick={() => setActiveTab('licenses')} icon={DollarSign}>
              Licenses
            </TabButton>
            <TabButton active={activeTab === 'operational'} onClick={() => setActiveTab('operational')} icon={Settings}>
              Operational
            </TabButton>
            <TabButton active={activeTab === 'financial'} onClick={() => setActiveTab('financial')} icon={TrendingUp}>
              Financial
            </TabButton>
            <TabButton active={activeTab === 'overcommit'} onClick={() => setActiveTab('overcommit')} icon={Layers}>
              Overcommit
            </TabButton>
            <TabButton active={activeTab === 'cloudpe'} onClick={() => setActiveTab('cloudpe')} icon={Package}>
              CloudPE Plans
            </TabButton>
            <TabButton active={activeTab === 'storage-pricing'} onClick={() => setActiveTab('storage-pricing')} icon={Database}>
              Storage Pricing
            </TabButton>
          </div>

          {/* Tab Content */}
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
            {activeTab === 'hardware' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Server Hardware Costs</h3>
                <SelectField
                  label="Hardware Cost Currency"
                  value={config.hardwareCurrency}
                  onChange={(v) => updateConfig('hardwareCurrency', v)}
                  options={[
                    { value: 'USD', label: 'USD ($)' },
                    { value: 'INR', label: 'INR (₹)' },
                    { value: 'EUR', label: 'EUR (€)' },
                  ]}
                />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="CPU Cost" value={config.cpuCost} onChange={(v) => updateConfig('cpuCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                  <InputField label="CPU Cores" value={config.cpuCores} onChange={(v) => updateConfig('cpuCores', v)} suffix="cores" />
                  <InputField label="RAM Cost" value={config.ramCost} onChange={(v) => updateConfig('ramCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                  <InputField label="RAM Size" value={config.ramGB} onChange={(v) => updateConfig('ramGB', v)} suffix="GB" />
                  <InputField label="NIC Cost" value={config.nicCost} onChange={(v) => updateConfig('nicCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                  <InputField label="Chassis Cost" value={config.chassisCost} onChange={(v) => updateConfig('chassisCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                  <InputField label="Other Hardware" value={config.otherHardwareCost} onChange={(v) => updateConfig('otherHardwareCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                </div>
              </div>
            )}

            {activeTab === 'storage' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Storage Configuration</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <SelectField label="Storage Type" value={config.storageType} onChange={(v) => updateConfig('storageType', v)}
                    options={[{ value: 'SSD', label: 'SSD (SATA)' }, { value: 'NVMe', label: 'NVMe' }, { value: 'HDD', label: 'HDD' }]} />
                  <InputField label="Cost per Disk" value={config.storageDiskCost} onChange={(v) => updateConfig('storageDiskCost', v)} prefix={currencySymbols[config.hardwareCurrency]} />
                  <InputField label="Number of Disks" value={config.storageDiskCount} onChange={(v) => updateConfig('storageDiskCount', v)} suffix="disks" />
                  <InputField label="Disk Size" value={config.storageDiskSizeGB} onChange={(v) => updateConfig('storageDiskSizeGB', v)} suffix="GB" />
                </div>
                <div className="mt-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                  <div className="text-sm text-slate-400">Total Raw Storage</div>
                  <div className="text-2xl font-bold text-cyan-400">{costs.totalStorageTB.toFixed(2)} TB</div>
                </div>
              </div>
            )}

            {activeTab === 'licenses' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">License Costs (Monthly)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="Virtuozzo Compute (per core/mo)" value={config.virtuozzoComputeLicensePerCore} onChange={(v) => updateConfig('virtuozzoComputeLicensePerCore', v)} prefix="$" />
                  <InputField label="Virtuozzo Storage (per TB/mo)" value={config.virtuozzoStorageLicensePerTB} onChange={(v) => updateConfig('virtuozzoStorageLicensePerTB', v)} prefix="$" />
                  <InputField label="Other Licenses (per mo)" value={config.otherLicenseCost} onChange={(v) => updateConfig('otherLicenseCost', v)} prefix="$" />
                </div>
                <div className="mt-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                  <div className="text-sm text-slate-400">Monthly License Cost</div>
                  <div className="text-xl font-bold text-purple-400">{formatCurrency(costs.monthlyLicenseCost)}</div>
                </div>
              </div>
            )}

            {activeTab === 'operational' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Operational Costs (Monthly)</h3>
                <SelectField label="Operational Currency" value={config.operationalCurrency} onChange={(v) => updateConfig('operationalCurrency', v)}
                  options={[{ value: 'INR', label: 'INR (₹)' }, { value: 'USD', label: 'USD ($)' }, { value: 'EUR', label: 'EUR (€)' }]} />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="Colocation" value={config.colocationCostMonthly} onChange={(v) => updateConfig('colocationCostMonthly', v)} prefix={currencySymbols[config.operationalCurrency]} />
                  <InputField label="Internet" value={config.internetCostMonthly} onChange={(v) => updateConfig('internetCostMonthly', v)} prefix={currencySymbols[config.operationalCurrency]} />
                  <InputField label="Support" value={config.supportCostMonthly} onChange={(v) => updateConfig('supportCostMonthly', v)} prefix={currencySymbols[config.operationalCurrency]} />
                  <InputField label="Sales Cost" value={config.salesCostPercentage} onChange={(v) => updateConfig('salesCostPercentage', v)} suffix="%" />
                  <InputField label="Power (Watts)" value={config.powerConsumptionWatts} onChange={(v) => updateConfig('powerConsumptionWatts', v)} suffix="W" />
                  <InputField label="Power Cost" value={config.powerCostPerKWH} onChange={(v) => updateConfig('powerCostPerKWH', v)} suffix="/kWh" />
                </div>
              </div>
            )}

            {activeTab === 'financial' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Financial Parameters</h3>

                {/* Loan/Finance EMI Section */}
                <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                  <h4 className="text-sm font-semibold text-amber-400 mb-3">Loan / Finance EMI</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <InputField label="Interest Rate" value={config.loanInterestRate} onChange={(v) => updateConfig('loanInterestRate', v)} suffix="% p.a." />
                    <InputField label="Finance Period" value={config.financePeriodMonths} onChange={(v) => updateConfig('financePeriodMonths', v)} suffix="months" />
                    <InputField label="Salvage Value" value={config.salvageValuePercentage} onChange={(v) => updateConfig('salvageValuePercentage', v)} suffix="%" />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-xs text-slate-500">Principal</div>
                      <div className="font-mono text-cyan-400">{formatCurrency(costs.principalToFinance)}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-xs text-slate-500">Salvage Value</div>
                      <div className="font-mono text-green-400">{formatCurrency(costs.salvageValue)}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-xs text-slate-500">Monthly EMI</div>
                      <div className="font-mono text-amber-400">{formatCurrency(costs.monthlyEMI)}</div>
                    </div>
                  </div>
                </div>

                {/* Other Financial Parameters */}
                <div className="grid grid-cols-2 gap-4">
                  <InputField label="HA Cost %" value={config.haPercentage} onChange={(v) => updateConfig('haPercentage', v)} suffix="%" />
                  <InputField label="Colocation Increase" value={config.annualColocationIncreasePercentage} onChange={(v) => updateConfig('annualColocationIncreasePercentage', v)} suffix="% p.a." />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  EMI = Equated Monthly Installment for financing hardware (includes interest).
                  Salvage Value = Expected residual value at end of finance period. Profit Margin is in Settings.
                </p>
              </div>
            )}

            {activeTab === 'overcommit' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Overcommit Ratios</h3>
                <p className="text-sm text-slate-400 mb-4">Determines how many virtual resources can be allocated relative to physical resources. Storage is handled separately in the Storage Pricing tab.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="CPU Overcommit" value={config.cpuOvercommitRatio} onChange={(v) => updateConfig('cpuOvercommitRatio', v)} suffix=":1" step="0.1" />
                  <InputField label="RAM Overcommit" value={config.ramOvercommitRatio} onChange={(v) => updateConfig('ramOvercommitRatio', v)} suffix=":1" step="0.1" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                    <div className="text-xs text-blue-300 uppercase">Available vCPU</div>
                    <div className="text-2xl font-bold text-blue-400">{costs.availableCPUCores}</div>
                    <div className="text-xs text-slate-500">from {config.cpuCores} physical</div>
                  </div>
                  <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                    <div className="text-xs text-blue-300 uppercase">Available RAM</div>
                    <div className="text-2xl font-bold text-blue-400">{costs.availableRAMGB.toFixed(1)} GB</div>
                    <div className="text-xs text-slate-500">from {config.ramGB} GB physical</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'cloudpe' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">CloudPE Plan Analysis</h3>
                    <p className="text-sm text-slate-400 mt-1">Cost comparison and capacity analysis for CloudPE plans</p>
                  </div>
                </div>

                {/* OS and Plan Type Filters */}
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">OS:</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedPlanOS('linux')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                          selectedPlanOS === 'linux'
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
                        }`}
                      >
                        <Monitor className="w-4 h-4 inline mr-1" /> Linux
                      </button>
                      <button
                        onClick={() => setSelectedPlanOS('windows')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                          selectedPlanOS === 'windows'
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
                        }`}
                      >
                        <Server className="w-4 h-4 inline mr-1" /> Windows
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Type:</span>
                    <div className="flex gap-1">
                      {[
                        { value: 'general_purpose', label: 'General Purpose' },
                        { value: 'cpu_intensive', label: 'CPU Intensive' },
                        { value: 'memory_optimized', label: 'Memory Optimized' },
                      ].map(type => (
                        <button
                          key={type.value}
                          onClick={() => setSelectedCloudPlanType(type.value)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                            selectedCloudPlanType === type.value
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Pricing Analysis Table */}
                {cloudpePlanAnalysis.length > 0 ? (
                  <div className="overflow-x-auto mt-4">
                    <p className="text-xs text-slate-500 mb-2">Hover over prices for calculation details. Storage excluded from capacity.</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700/50">
                          <th className="py-3 px-2 text-left">Flavour</th>
                          <th className="py-3 px-2 text-center">vCPU</th>
                          <th className="py-3 px-2 text-center">RAM</th>
                          <th className="py-3 px-2 text-right">Cost Price</th>
                          <th className="py-3 px-2 text-right">Our Sell</th>
                          <th className="py-3 px-2 text-right">CloudPE</th>
                          <th className="py-3 px-2 text-right">Margin</th>
                          <th className="py-3 px-2 text-center">Max VMs</th>
                          <th className="py-3 px-2 text-left">Bottleneck</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cloudpePlanAnalysis.map(plan => (
                          <tr key={plan.id} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                            <td className="py-3 px-2 font-mono text-slate-200">{plan.flavour}</td>
                            <td className="py-3 px-2 text-center text-slate-300">{plan.vcpu}</td>
                            <td className="py-3 px-2 text-center text-slate-300">{plan.ram_gb} GB</td>
                            <td className="py-3 px-2 text-right">
                              <CalcTooltip
                                value={plan.costPrice}
                                explanation={`Cost = Total monthly operating cost ÷ max VMs that fit, plus ${config.salesCostPercentage}% sales cost. Storage excluded from capacity.`}
                                breakdown={[
                                  { label: 'Total Monthly Cost', value: costs.totalMonthlyOperatingCost },
                                  { label: 'Max VMs (by CPU)', value: `${plan.maxByVCPU} VMs` },
                                  { label: 'Max VMs (by RAM)', value: `${plan.maxByRAM} VMs` },
                                  { label: 'Limiting Factor', value: plan.bottleneck },
                                  { label: 'Max VMs', value: `${plan.maxVMs} VMs` },
                                  { label: 'Base Cost per VM', value: plan.baseCostPerVM },
                                  { label: `Sales Cost (${config.salesCostPercentage}%)`, value: plan.salesCost },
                                ]}
                                formatFn={formatCurrency}
                              >
                                <span className="font-mono text-slate-300">{formatCurrency(plan.costPrice)}</span>
                              </CalcTooltip>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <CalcTooltip
                                value={plan.sellingPrice}
                                explanation={`Selling price = Cost price × (1 + ${settings.profitMarginPercentage}% profit margin).`}
                                breakdown={[
                                  { label: 'Cost Price', value: plan.costPrice },
                                  { label: `Profit Margin (${settings.profitMarginPercentage}%)`, value: plan.sellingPrice - plan.costPrice },
                                ]}
                                formatFn={formatCurrency}
                              >
                                <span className="font-mono text-amber-400">{formatCurrency(plan.sellingPrice)}</span>
                              </CalcTooltip>
                            </td>
                            <td className="py-3 px-2 text-right font-mono text-cyan-400">{formatCurrency(plan.cloudpePrice)}</td>
                            <td className="py-3 px-2 text-right">
                              <CalcTooltip
                                value={plan.marginPercentage}
                                explanation={`Margin = (CloudPE price - Our cost) ÷ Our cost × 100. ${plan.marginPercentage >= 0 ? 'Positive margin means CloudPE price covers our costs plus profit.' : 'Negative margin means CloudPE price is below our cost.'}`}
                                breakdown={[
                                  { label: 'CloudPE Price', value: plan.cloudpePrice },
                                  { label: 'Our Cost Price', value: plan.costPrice },
                                  { label: 'Margin Amount', value: plan.marginAmount },
                                ]}
                                formatFn={formatCurrency}
                              >
                                <span className={`font-mono ${plan.marginPercentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {plan.marginPercentage >= 0 ? '+' : ''}{plan.marginPercentage.toFixed(1)}%
                                </span>
                              </CalcTooltip>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <CalcTooltip
                                value={plan.maxVMs}
                                explanation={`Maximum VMs = min(available vCPU ÷ plan vCPU, available RAM ÷ plan RAM). Storage excluded from calculation.`}
                                breakdown={[
                                  { label: `Available vCPU (${costs.availableCPUCores})`, value: `÷ ${plan.vcpu} = ${plan.maxByVCPU} VMs` },
                                  { label: `Available RAM (${costs.availableRAMGB.toFixed(1)} GB)`, value: `÷ ${plan.ram_gb} = ${plan.maxByRAM} VMs` },
                                  { label: 'Limiting Factor', value: plan.bottleneck },
                                ]}
                                formatFn={(v) => typeof v === 'number' ? v.toString() : v}
                              >
                                <span className="font-bold text-purple-400">{plan.maxVMs}</span>
                              </CalcTooltip>
                            </td>
                            <td className="py-3 px-2 text-left">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                plan.bottleneck === 'vCPU' ? 'bg-blue-500/20 text-blue-400' :
                                plan.bottleneck === 'RAM' ? 'bg-green-500/20 text-green-400' :
                                'bg-slate-700 text-slate-400'
                              }`}>
                                {plan.bottleneck}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    {cloudpePlans.length === 0 ? 'No CloudPE plans loaded. Check Plans page.' : 'No plans match the selected filters.'}
                  </div>
                )}

                {/* Resource Utilization Summary */}
                {cloudpePlanAnalysis.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-slate-700/50">
                    <h4 className="text-sm font-semibold text-slate-300 mb-4">Resource Utilization Analysis (for top plan)</h4>
                    {(() => {
                      const topPlan = cloudpePlanAnalysis.reduce((best, plan) =>
                        plan.potentialProfit > (best?.potentialProfit || 0) ? plan : best, null);
                      if (!topPlan || topPlan.maxVMs === 0) return <div className="text-slate-500">Configure server resources first.</div>;
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-slate-800/30 rounded-xl p-4">
                            <div className="text-sm text-slate-400 mb-2">Best Plan: <span className="text-amber-400 font-mono">{topPlan.flavour}</span></div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <CalcTooltip
                                  value={topPlan.maxVMs}
                                  explanation="Number of this flavor that can be deployed on the server, limited by CPU or RAM (storage excluded)."
                                  breakdown={[
                                    { label: `By vCPU (${costs.availableCPUCores} ÷ ${topPlan.vcpu})`, value: `${topPlan.maxByVCPU} VMs` },
                                    { label: `By RAM (${costs.availableRAMGB.toFixed(1)} ÷ ${topPlan.ram_gb})`, value: `${topPlan.maxByRAM} VMs` },
                                    { label: 'Bottleneck', value: topPlan.bottleneck },
                                  ]}
                                  formatFn={(v) => typeof v === 'number' ? v.toString() : v}
                                >
                                  <div>
                                    <div className="text-slate-500">Max VMs</div>
                                    <div className="text-lg font-bold text-purple-400">{topPlan.maxVMs}</div>
                                  </div>
                                </CalcTooltip>
                              </div>
                              <div>
                                <CalcTooltip
                                  value={topPlan.potentialProfit}
                                  explanation="Monthly profit if all VMs are sold at CloudPE price."
                                  breakdown={[
                                    { label: 'Revenue (VMs × CloudPE price)', value: topPlan.potentialRevenue },
                                    { label: 'Cost (VMs × Cost price)', value: topPlan.potentialCost },
                                  ]}
                                  formatFn={formatCurrency}
                                >
                                  <div>
                                    <div className="text-slate-500">Potential Profit/mo</div>
                                    <div className="text-lg font-bold text-emerald-400">{formatCurrency(topPlan.potentialProfit)}</div>
                                  </div>
                                </CalcTooltip>
                              </div>
                            </div>
                          </div>
                          <div className="bg-slate-800/30 rounded-xl p-4">
                            <div className="text-sm text-slate-400 mb-2">Unused Resources (Waste)</div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <CalcTooltip
                                value={topPlan.wastedVCPU}
                                explanation="vCPUs remaining after deploying max VMs."
                                breakdown={[
                                  { label: 'Available vCPU', value: `${costs.availableCPUCores}` },
                                  { label: `Used (${topPlan.maxVMs} × ${topPlan.vcpu})`, value: `${topPlan.usedVCPU}` },
                                  { label: 'Utilization', value: `${topPlan.vcpuUtilization.toFixed(0)}%` },
                                ]}
                                formatFn={(v) => v}
                              >
                                <div>
                                  <div className="text-slate-500">vCPU</div>
                                  <div className={`font-mono ${topPlan.wastedVCPU > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    {topPlan.wastedVCPU.toFixed(0)} ({(100 - topPlan.vcpuUtilization).toFixed(0)}%)
                                  </div>
                                </div>
                              </CalcTooltip>
                              <CalcTooltip
                                value={topPlan.wastedRAM}
                                explanation="RAM remaining after deploying max VMs."
                                breakdown={[
                                  { label: 'Available RAM', value: `${costs.availableRAMGB.toFixed(1)} GB` },
                                  { label: `Used (${topPlan.maxVMs} × ${topPlan.ram_gb})`, value: `${topPlan.usedRAM.toFixed(1)} GB` },
                                  { label: 'Utilization', value: `${topPlan.ramUtilization.toFixed(0)}%` },
                                ]}
                                formatFn={(v) => v}
                              >
                                <div>
                                  <div className="text-slate-500">RAM</div>
                                  <div className={`font-mono ${topPlan.wastedRAM > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    {topPlan.wastedRAM.toFixed(1)} GB ({(100 - topPlan.ramUtilization).toFixed(0)}%)
                                  </div>
                                </div>
                              </CalcTooltip>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'storage-pricing' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Storage Pricing by Redundancy Type</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Compare usable storage and per-GB pricing across different redundancy schemes. License cost is calculated per usable TB.
                  </p>
                </div>

                {/* Raw Storage Info */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                    <div className="text-xs text-slate-500 uppercase">Raw Storage</div>
                    <div className="text-xl font-bold text-slate-200">{(costs.rawStorageGB / 1024).toFixed(2)} TB</div>
                    <div className="text-xs text-slate-500">{config.storageDiskCount} × {config.storageDiskSizeGB} GB</div>
                  </div>
                  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                    <div className="text-xs text-slate-500 uppercase">Infra Cost/mo</div>
                    <div className="text-xl font-bold text-orange-400">{formatCurrency(costs.storageMonthlyInfraCost)}</div>
                    <div className="text-xs text-slate-500">EMI + Colo + Power</div>
                  </div>
                  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                    <div className="text-xs text-slate-500 uppercase">License Cost</div>
                    <div className="text-xl font-bold text-purple-400">{formatCurrency(costs.licenseCostPerTBMonth)}/TB</div>
                    <div className="text-xs text-slate-500">Per usable TB</div>
                  </div>
                  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                    <div className="text-xs text-slate-500 uppercase">Storage License</div>
                    <div className="text-xl font-bold text-slate-200">${config.virtuozzoStorageLicensePerTB}/TB</div>
                    <div className="text-xs text-slate-500">Input rate</div>
                  </div>
                  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                    <div className="text-xs text-slate-500 uppercase">Profit Margin</div>
                    <div className="text-xl font-bold text-emerald-400">{settings.profitMarginPercentage}%</div>
                    <div className="text-xs text-slate-500">On total cost</div>
                  </div>
                </div>

                {/* Pricing Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700/50">
                        <th className="py-3 px-2 text-left">Redundancy</th>
                        <th className="py-3 px-2 text-center">Overhead</th>
                        <th className="py-3 px-2 text-right">Usable</th>
                        <th className="py-3 px-2 text-right">Infra/GB</th>
                        <th className="py-3 px-2 text-right">License/GB</th>
                        <th className="py-3 px-2 text-right">Total/GB</th>
                        <th className="py-3 px-2 text-right">Sell/GB</th>
                        <th className="py-3 px-2 text-right">Sell/TB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.storageRedundancyPricing.map((type, idx) => (
                        <tr
                          key={type.id}
                          className={`border-b border-slate-800/30 hover:bg-slate-800/20 ${
                            idx === 0 ? 'bg-red-500/5' : idx === costs.storageRedundancyPricing.length - 1 ? 'bg-emerald-500/5' : ''
                          }`}
                        >
                          <td className="py-3 px-2">
                            <div className="font-medium text-slate-200">{type.name}</div>
                            <div className="text-xs text-slate-500">{type.description}</div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-1 rounded text-sm font-mono ${
                              type.overhead >= 100 ? 'bg-red-500/20 text-red-400' :
                              type.overhead >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {type.overhead}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <CalcTooltip
                              value={type.usableStorageGB}
                              explanation={`Usable = Raw ÷ (1 + overhead%). With ${type.overhead}% overhead, ${(100 / (100 + type.overhead) * 100).toFixed(1)}% of raw is usable.`}
                              breakdown={[
                                { label: 'Raw Storage', value: `${type.rawStorageTB.toFixed(2)} TB` },
                                { label: `Overhead (${type.overhead}%)`, value: `${(type.rawStorageTB - type.usableStorageTB).toFixed(2)} TB` },
                              ]}
                              formatFn={(v) => typeof v === 'number' ? `${(v / 1024).toFixed(2)} TB` : v}
                            >
                              <span className="font-mono text-cyan-400">{type.usableStorageTB.toFixed(2)} TB</span>
                            </CalcTooltip>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <CalcTooltip
                              value={type.infraCostPerGBMonth}
                              explanation="Infrastructure cost per GB = Storage infra monthly cost (EMI + colo + support + power) ÷ Usable storage. Does NOT include license."
                              breakdown={[
                                { label: 'Storage Infra Cost', value: costs.storageMonthlyInfraCost },
                                { label: 'Usable Storage', value: `${type.usableStorageGB.toLocaleString()} GB` },
                              ]}
                              formatFn={formatCurrency}
                            >
                              <span className="font-mono text-orange-400">{formatCurrency(type.infraCostPerGBMonth)}</span>
                            </CalcTooltip>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <CalcTooltip
                              value={type.licensePerGBMonth}
                              explanation={`License per GB = License per TB (${formatCurrency(costs.licenseCostPerTBMonth)}) ÷ 1024. Same for all redundancy types.`}
                              breakdown={[
                                { label: 'License per TB', value: costs.licenseCostPerTBMonth },
                                { label: '÷ 1024', value: type.licensePerGBMonth },
                              ]}
                              formatFn={formatCurrency}
                            >
                              <span className="font-mono text-purple-400">{formatCurrency(type.licensePerGBMonth)}</span>
                            </CalcTooltip>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <CalcTooltip
                              value={type.totalCostPerGBMonth}
                              explanation="Total cost per GB = Infrastructure + License. This is your break-even cost."
                              breakdown={[
                                { label: 'Infra per GB', value: type.infraCostPerGBMonth },
                                { label: 'License per GB', value: type.licensePerGBMonth },
                              ]}
                              formatFn={formatCurrency}
                            >
                              <span className="font-mono text-slate-300">{formatCurrency(type.totalCostPerGBMonth)}</span>
                            </CalcTooltip>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <CalcTooltip
                              value={type.sellPerGBMonth}
                              explanation={`Sell price = Total cost × (1 + ${settings.profitMarginPercentage}% margin).`}
                              breakdown={[
                                { label: 'Infra per GB', value: type.infraCostPerGBMonth },
                                { label: 'License per GB', value: type.licensePerGBMonth },
                                { label: 'Total Cost', value: type.totalCostPerGBMonth },
                                { label: `Profit (${settings.profitMarginPercentage}%)`, value: type.sellPerGBMonth - type.totalCostPerGBMonth },
                              ]}
                              formatFn={formatCurrency}
                            >
                              <span className="font-mono text-emerald-400 font-semibold">{formatCurrency(type.sellPerGBMonth)}</span>
                            </CalcTooltip>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <span className="font-mono text-amber-400 font-semibold">{formatCurrency(type.sellPerTBMonth)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary Box */}
                <div className="mt-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                  <h4 className="text-sm font-semibold text-slate-300 mb-3">Efficiency Comparison</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {(() => {
                      const bestEfficiency = costs.storageRedundancyPricing[costs.storageRedundancyPricing.length - 1];
                      const worstEfficiency = costs.storageRedundancyPricing[0];
                      const savings = worstEfficiency.sellPerGBMonth - bestEfficiency.sellPerGBMonth;
                      const savingsPercent = worstEfficiency.sellPerGBMonth > 0
                        ? ((savings / worstEfficiency.sellPerGBMonth) * 100).toFixed(0)
                        : 0;
                      return (
                        <>
                          <div>
                            <div className="text-slate-500">Most Efficient</div>
                            <div className="font-semibold text-emerald-400">{bestEfficiency.name}</div>
                            <div className="text-xs text-slate-500">{bestEfficiency.overhead}% overhead</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Least Efficient</div>
                            <div className="font-semibold text-red-400">{worstEfficiency.name}</div>
                            <div className="text-xs text-slate-500">{worstEfficiency.overhead}% overhead</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Price Difference</div>
                            <div className="font-semibold text-amber-400">{formatCurrency(savings)}/GB</div>
                            <div className="text-xs text-slate-500">{savingsPercent}% savings</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Usable Gain (EC 17+3)</div>
                            <div className="font-semibold text-cyan-400">
                              +{(bestEfficiency.usableStorageTB - worstEfficiency.usableStorageTB).toFixed(2)} TB
                            </div>
                            <div className="text-xs text-slate-500">vs 2x replication</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-4">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded bg-red-500/20 border border-red-500/50"></span>
                    <span>High overhead (100%+)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/50"></span>
                    <span>Medium overhead (50-99%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/50"></span>
                    <span>Low overhead (&lt;50%)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* VM Plans */}
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">VM Plans</h3>
                <p className="text-xs text-slate-500 mt-1">Storage excluded from pricing - hover over cost/sell for details</p>
              </div>
              <button onClick={addVMPlan}
                className="px-3 py-1.5 text-sm bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all border border-emerald-500/30">
                <Plus className="w-4 h-4 inline mr-1" /> Add Plan
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700/50">
                    <th className="py-3 px-2 text-left">Name</th>
                    <th className="py-3 px-2 text-center">vCPU</th>
                    <th className="py-3 px-2 text-center">RAM</th>
                    <th className="py-3 px-2 text-center">BW</th>
                    <th className="py-3 px-2 text-center">IPs</th>
                    <th className="py-3 px-2 text-right">Cost</th>
                    <th className="py-3 px-2 text-right">Sell</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {vmPlans.map(plan => {
                    const pricing = calculateVMPrice(plan);
                    return (
                      <tr key={plan.id} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                        <td className="py-3 px-2">
                          <input type="text" value={plan.name} onChange={(e) => updateVMPlan(plan.id, 'name', e.target.value)}
                            className="bg-transparent border-none text-slate-200 focus:outline-none w-24" />
                        </td>
                        <td className="py-3 px-2">
                          <input type="number" value={plan.vCPU} onChange={(e) => updateVMPlan(plan.id, 'vCPU', parseInt(e.target.value) || 0)} min="1"
                            className="w-14 bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-center text-slate-200 focus:outline-none focus:border-amber-500/50" />
                        </td>
                        <td className="py-3 px-2">
                          <input type="number" value={plan.ramGB} onChange={(e) => updateVMPlan(plan.id, 'ramGB', parseFloat(e.target.value) || 0)} min="0.5" step="0.5"
                            className="w-14 bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-center text-slate-200 focus:outline-none focus:border-amber-500/50" />
                        </td>
                        <td className="py-3 px-2">
                          <input type="number" value={plan.bandwidthTBMonthly} onChange={(e) => updateVMPlan(plan.id, 'bandwidthTBMonthly', parseFloat(e.target.value) || 0)} min="0" step="0.5"
                            className="w-14 bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-center text-slate-200 focus:outline-none focus:border-amber-500/50" />
                        </td>
                        <td className="py-3 px-2">
                          <input type="number" value={plan.ipAddresses} onChange={(e) => updateVMPlan(plan.id, 'ipAddresses', parseInt(e.target.value) || 0)} min="0"
                            className="w-12 bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-center text-slate-200 focus:outline-none focus:border-amber-500/50" />
                        </td>
                        <td className="py-3 px-2 text-right">
                          <CalcTooltip
                            value={pricing.costPrice}
                            explanation={`Cost price = (CPU cost + RAM cost + Bandwidth + IPs) × (1 + ${config.salesCostPercentage}% sales cost).`}
                            breakdown={[
                              { label: `CPU (${plan.vCPU} × ${formatCurrency(pricing.cpuCostPerCore)})`, value: pricing.cpuCost },
                              { label: `RAM (${plan.ramGB} GB × ${formatCurrency(pricing.ramCostPerGB)})`, value: pricing.ramCost },
                              { label: 'Compute Subtotal', value: pricing.baseCost },
                              { label: `Bandwidth (${plan.bandwidthTBMonthly} TB)`, value: pricing.bandwidthCost },
                              { label: `IP Addresses (${plan.ipAddresses})`, value: pricing.ipCost },
                              { label: `Sales Cost (${config.salesCostPercentage}%)`, value: pricing.salesCost },
                            ]}
                            formatFn={formatCurrency}
                          >
                            <span className="font-mono text-slate-300">{formatCurrency(pricing.costPrice)}</span>
                          </CalcTooltip>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <CalcTooltip
                            value={pricing.sellingPrice}
                            explanation={`Selling price = Cost price × (1 + ${settings.profitMarginPercentage}% profit margin).`}
                            breakdown={[
                              { label: 'Cost Price', value: pricing.costPrice },
                              { label: `Profit Margin (${settings.profitMarginPercentage}%)`, value: pricing.sellingPrice - pricing.costPrice },
                            ]}
                            formatFn={formatCurrency}
                          >
                            <span className="font-mono font-bold text-emerald-400">{formatCurrency(pricing.sellingPrice)}</span>
                          </CalcTooltip>
                        </td>
                        <td className="py-3 px-2">
                          {vmPlans.length > 1 && (
                            <button onClick={() => deleteVMPlan(plan.id)} className="text-red-400/60 hover:text-red-400 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Cost Summary</h3>
            <p className="text-xs text-slate-500 mb-3">Hover over values for details. Costs split: Compute vs Storage.</p>

            {/* Capital Costs */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500">Compute Hardware</span>
                <CalcTooltip
                  value={costs.computeHardwareCost}
                  explanation="Server hardware excluding storage: CPU, RAM, NIC, chassis, and other hardware."
                  breakdown={[
                    { label: 'CPU', value: convertToTargetCurrency(config.cpuCost, config.hardwareCurrency) },
                    { label: 'RAM', value: convertToTargetCurrency(config.ramCost, config.hardwareCurrency) },
                    { label: 'NIC + Chassis + Other', value: convertToTargetCurrency(config.nicCost + config.chassisCost + config.otherHardwareCost, config.hardwareCurrency) },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-slate-300">{formatCurrency(costs.computeHardwareCost)}</span>
                </CalcTooltip>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500">Storage Hardware</span>
                <CalcTooltip
                  value={costs.storageHardwareCost}
                  explanation={`Storage disks only: ${config.storageDiskCount} × ${currencySymbols[config.hardwareCurrency]}${config.storageDiskCost}`}
                  breakdown={[
                    { label: `${config.storageDiskCount} disks × ${currencySymbols[config.hardwareCurrency]}${config.storageDiskCost}`, value: costs.storageHardwareCost },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-slate-300">{formatCurrency(costs.storageHardwareCost)}</span>
                </CalcTooltip>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500">HA Reserve ({config.haPercentage}%)</span>
                <CalcTooltip
                  value={costs.haCost}
                  explanation={`HA reserve applies only to COMPUTE hardware (not storage): ${config.haPercentage}% of compute cost.`}
                  breakdown={[
                    { label: 'Compute Hardware', value: costs.computeHardwareCost },
                    { label: `× ${config.haPercentage}%`, value: costs.haCost },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-slate-300">{formatCurrency(costs.haCost)}</span>
                </CalcTooltip>
              </div>
              <div className="flex justify-between items-center py-1 border-t border-slate-800/50">
                <span className="text-slate-400">Compute Capital</span>
                <CalcTooltip
                  value={costs.computeCapitalCost}
                  explanation="Compute hardware + HA reserve. Used for VM pricing."
                  breakdown={[
                    { label: 'Compute Hardware', value: costs.computeHardwareCost },
                    { label: 'HA Reserve', value: costs.haCost },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-blue-400">{formatCurrency(costs.computeCapitalCost)}</span>
                </CalcTooltip>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-400">Storage Capital</span>
                <CalcTooltip
                  value={costs.storageCapitalCost}
                  explanation="Storage hardware only (no HA). Used for storage add-on pricing."
                  breakdown={[
                    { label: 'Storage Hardware', value: costs.storageHardwareCost },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-orange-400">{formatCurrency(costs.storageCapitalCost)}</span>
                </CalcTooltip>
              </div>
              <div className="flex justify-between items-center py-1 border-t border-slate-700/50">
                <span className="text-slate-300 font-semibold">Total Capital</span>
                <span className="font-mono text-amber-400 font-bold">{formatCurrency(costs.totalCapitalCost)}</span>
              </div>
            </div>

            {/* Monthly Costs - Split View */}
            <div className="mt-6 pt-4 border-t border-slate-700/50">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Monthly Operating Costs</h4>

              {/* Compute Monthly */}
              <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="text-xs text-blue-400 font-semibold mb-2">COMPUTE (for VM pricing)</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">EMI</span>
                    <CalcTooltip
                      value={costs.computeMonthlyEMI}
                      explanation="EMI on compute capital (with HA). Used for VM pricing."
                      breakdown={[
                        { label: 'Compute Capital', value: costs.computeCapitalCost },
                        { label: `Salvage (${config.salvageValuePercentage}%)`, value: costs.computeSalvageValue },
                        { label: 'Principal', value: costs.computePrincipalToFinance },
                      ]}
                      formatFn={formatCurrency}
                    >
                      <span className="font-mono text-slate-300">{formatCurrency(costs.computeMonthlyEMI)}</span>
                    </CalcTooltip>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Colocation (80%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.computeColocationCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Internet (100%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.computeInternetCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Support (80%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.computeSupportCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Power (90%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.computePowerCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Licenses</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.computeMonthlyLicenseCost)}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-blue-500/30 font-semibold">
                    <span className="text-blue-300">Compute Total</span>
                    <span className="font-mono text-blue-400">{formatCurrency(costs.computeMonthlyOperatingCost)}</span>
                  </div>
                </div>
              </div>

              {/* Storage Monthly */}
              <div className="mb-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                <div className="text-xs text-orange-400 font-semibold mb-2">STORAGE INFRA (license in Storage Pricing tab)</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">EMI</span>
                    <CalcTooltip
                      value={costs.storageMonthlyEMI}
                      explanation="EMI on storage capital only (no HA)."
                      breakdown={[
                        { label: 'Storage Capital', value: costs.storageCapitalCost },
                        { label: `Salvage (${config.salvageValuePercentage}%)`, value: costs.storageSalvageValue },
                        { label: 'Principal', value: costs.storagePrincipalToFinance },
                      ]}
                      formatFn={formatCurrency}
                    >
                      <span className="font-mono text-slate-300">{formatCurrency(costs.storageMonthlyEMI)}</span>
                    </CalcTooltip>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Colocation (20%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.storageColocationCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Support (20%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.storageSupportCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Power (10%)</span>
                    <span className="font-mono text-slate-300">{formatCurrency(costs.storagePowerCost)}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-orange-500/30 font-semibold">
                    <span className="text-orange-300">Storage Infra</span>
                    <span className="font-mono text-orange-400">{formatCurrency(costs.storageMonthlyInfraCost)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">License: {formatCurrency(costs.licenseCostPerTBMonth)}/TB usable (see Storage Pricing)</div>
                </div>
              </div>

              {/* Grand Total */}
              <div className="flex justify-between pt-2 border-t border-slate-700/50">
                <span className="text-slate-300 font-semibold">Total Monthly</span>
                <CalcTooltip
                  value={costs.totalMonthlyOperatingCost}
                  explanation="Sum of compute monthly + storage infra + storage license."
                  breakdown={[
                    { label: 'Compute Monthly', value: costs.computeMonthlyOperatingCost },
                    { label: 'Storage Infra', value: costs.storageMonthlyInfraCost },
                    { label: 'Storage License', value: costs.storageMonthlyLicenseCost },
                  ]}
                  formatFn={formatCurrency}
                >
                  <span className="font-mono text-amber-400 font-bold">{formatCurrency(costs.totalMonthlyOperatingCost)}</span>
                </CalcTooltip>
              </div>
            </div>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Resource Unit Costs</h3>

            {/* Compute Resources */}
            <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <div className="text-xs text-blue-400 font-semibold mb-2">COMPUTE (VM Base Price)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <div className="text-xs text-slate-400 uppercase">Per vCPU / month</div>
                  <CalcTooltip
                    value={costs.cpuCostPerCoreMonth}
                    explanation={`55% of COMPUTE monthly cost allocated to CPU, divided by available vCPUs.`}
                    breakdown={[
                      { label: 'Compute Monthly Cost', value: costs.computeMonthlyOperatingCost },
                      { label: 'CPU Allocation (55%)', value: costs.computeMonthlyOperatingCost * 0.55 },
                      { label: 'Physical Cores', value: `${config.cpuCores} cores` },
                      { label: 'Overcommit Ratio', value: `${config.cpuOvercommitRatio}:1` },
                      { label: 'Available vCPUs', value: `${costs.availableCPUCores} vCPUs` },
                    ]}
                    formatFn={formatCurrency}
                  >
                    <div className="text-lg font-bold text-blue-400">{formatCurrency(costs.cpuCostPerCoreMonth)}</div>
                  </CalcTooltip>
                </div>
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <div className="text-xs text-slate-400 uppercase">Per GB RAM / month</div>
                  <CalcTooltip
                    value={costs.ramCostPerGBMonth}
                    explanation={`45% of COMPUTE monthly cost allocated to RAM, divided by available GB.`}
                    breakdown={[
                      { label: 'Compute Monthly Cost', value: costs.computeMonthlyOperatingCost },
                      { label: 'RAM Allocation (45%)', value: costs.computeMonthlyOperatingCost * 0.45 },
                      { label: 'Physical RAM', value: `${config.ramGB} GB` },
                      { label: 'Overcommit Ratio', value: `${config.ramOvercommitRatio}:1` },
                      { label: 'Available RAM', value: `${costs.availableRAMGB.toFixed(1)} GB` },
                    ]}
                    formatFn={formatCurrency}
                  >
                    <div className="text-lg font-bold text-blue-400">{formatCurrency(costs.ramCostPerGBMonth)}</div>
                  </CalcTooltip>
                </div>
              </div>
            </div>

            {/* Storage Note */}
            <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <div className="text-xs text-orange-400 font-semibold mb-1">STORAGE (Add-on Price)</div>
              <p className="text-sm text-slate-400">
                Storage pricing varies by redundancy type. See the <span className="text-orange-400 font-medium">Storage Pricing</span> tab for per-GB costs including infra and license.
              </p>
            </div>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">5-Year Projections</h3>
            <div className="space-y-2">
              {costs.futureProjections.map(proj => (
                <div key={proj.year} className="flex justify-between items-center py-2 border-b border-slate-800/30">
                  <span className="text-slate-400">Year {proj.year}</span>
                  <div className="text-right">
                    <div className="font-mono text-slate-200">{formatCurrency(proj.yearlyOperatingCost)}/yr</div>
                    <div className="text-xs text-slate-500">Colo: {formatCurrency(proj.monthlyColocation)}/mo</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigEditorPage;
